import { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "src/hooks/useRouter";
import JoinUI from "./Join.presenter";
import { notifyWarning } from "src/components/Common/Toast";
import { useApiError } from "src/hooks/useApiError";
import { useJoin } from "src/hooks/api/auth/useJoin";
import { useSendToken } from "src/hooks/api/auth/useSendToken";
import { useCheckUserId } from "src/hooks/api/auth/useCheckUserId";
import { JoinFormData, joinSchema } from "./Join.schema";
import { useCheckToken } from "src/hooks/api/auth/useCheckToken";
import { useTranslation } from "react-i18next";

interface IJoinContainerProps {}

const JoinContainer = () => {
  // 추가 상태 관리
  const [checkUserid, setCheckUserid] = useState<string>("FALSE");
  const [msgModalTitle, setMsgModalTitle] = useState<string>("");
  const [msgModalBody, setMsgModalBody] = useState<string>("");
  const [showMsgModal, setShowMsgModal] = useState<boolean>(false);

  const route = useRouter();
  const { t } = useTranslation();
  // React Hook Form 설정
  const joinForm = useForm<JoinFormData>({
    resolver: yupResolver(joinSchema),
    defaultValues: {
      userId: "",
      userName: "",
      companyName: "",
      password: "",
      passwordCheck: "",
      token: "",
      isConfirmConditions: false,
    },
    mode: "onChange", // onChange로 변경하여 실시간 감지
  });
  const { formState, setError } = joinForm;
  const { handleError } = useApiError({
    // 500: {
    //   INVALID_TOKEN: () => {
    //     setCheckToken("INVALID_TOKEN");
    //   },
    //   DUPLICATE_DATA: () => {
    //     setCheckUserid("DUPLICATE_DATA");
    //   },
    //   NOT_MATCH_ID: () => {
    //     setCheckUserid("NOT_MATCH_ID");
    //   },
    //   default: (err: any) => {
    //     notifyError(err?.response?.data?.errorMessage ?? "error");
    //   },
    // },
  });

  // 회원가입 요청
  const { mutateJoin } = useJoin({
    onSuccess: () => {
      setShowMsgModal(true);
      setMsgModalTitle(t("logIn.signUp.completedTitle"));
      setMsgModalBody(t("logIn.signUp.completed"));
    },
    onError: () => handleError,
  });

  // 이메일 인증 토큰
  const {
    isSuccess: isSendTokenSuccess,
    isSendTokenLoading,
    mutateSendToken,
  } = useSendToken({
    onError: () => handleError,
  });
  // 인증 토큰 확인
  const {
    isSuccess: isCheckTokenSuccess,
    isCheckTokenLoading,
    mutateCheckToken,
  } = useCheckToken({});

  // 이메일 유효성 검증
  const { mutateCheckUserId } = useCheckUserId({
    onSuccess: () => {
      setCheckUserid("TRUE");
    },
    onError: () => {
      setCheckUserid("FALSE");
      setError("userId", {
        message: t("logIn.signUp.usedId"),
      });
      return handleError;
    },
  });

  // watch를 사용하여 userId 값 실시간 감지
  const userId = joinForm.watch("userId");

  // userId 변경 시 디바운싱된 중복 확인
  useEffect(() => {
    if (userId) {
      mutateCheckUserId({ userId });
    }
  }, [userId, mutateCheckUserId]);

  // 토큰 에러 상태 리셋
  // useEffect(() => {
  //   if (checkToken) {
  //     setCheckToken("");
  //   }
  // }, [userId]);

  const nextButton = () => {
    setShowMsgModal(false);
    route.goLogin();
  };

  // 회원가입 폼 제출 핸들러
  const onJoinSubmit = (data: JoinFormData) => {
    mutateJoin({
      userId: data.userId,
      userName: data.userName,
      userPwd: data.password,
      token: data.token,
      companyName: data.companyName,
    });
  };

  // 이메일 인증 토큰 전송
  const sendToken = async () => {
    if (!userId) {
      notifyWarning(t("logIn.enterUserEmail"));
      return;
    }

    if (checkUserid !== "TRUE") {
      notifyWarning("이메일 중복 확인을 완료해주세요.");
      return;
    }

    mutateSendToken({ userId });
  };
  const checkToken = () => {
    mutateCheckToken({
      userId: joinForm.getValues("userId"),
      token: joinForm.getValues("token"),
    }).catch((err) => {
      setError("token", {
        message: t("logIn.signUp.otp.mismatchMsg"),
      });
      // console.log("err", err);
    });
  };
  // 폼 유효성 및 가입 가능 여부
  const joinPossible = joinForm.formState.isValid && checkUserid === "TRUE";

  return (
    <FormProvider {...joinForm}>
      <JoinUI
        // 폼 관련
        joinForm={joinForm}
        onJoinSubmit={onJoinSubmit}
        joinPossible={joinPossible}
        // 상태
        msgModalTitle={msgModalTitle}
        msgModalBody={msgModalBody}
        showMsgModal={showMsgModal}
        isSendTokenLoading={isSendTokenLoading}
        isSendTokenSuccess={isSendTokenSuccess}
        isCheckTokenLoading={isCheckTokenLoading}
        isCheckTokenSuccess={isCheckTokenSuccess}
        // 핸들러
        sendToken={sendToken}
        checkToken={checkToken}
        nextButton={nextButton}
      />
    </FormProvider>
  );
};

export default JoinContainer;
